import { createContext, useCallback, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/auth';
import { onboardingKeys, useOnboardingStatusQuery } from './queries';
import type { OnboardingStatus } from './schemas';

type OnboardingContextValue = {
  status: OnboardingStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  applyStatus: (status: OnboardingStatus) => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const workspaceId = useAuthStore((s) => s.workspace?.id ?? null);
  const query = useOnboardingStatusQuery();

  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);

  const applyStatus = useCallback(
    (next: OnboardingStatus) => {
      // Write into the current workspace's cache slot. If the caller invoked
      // `applyStatus` outside an authenticated session (workspaceId === null),
      // the write lands in the `'unauth'` slot — harmless, and the next
      // authenticated render will key on the real workspace and refetch.
      qc.setQueryData(onboardingKeys.status(workspaceId), next);
    },
    [qc, workspaceId],
  );

  // 401 here means "not authenticated yet" — the caller (AppShell) gates this
  // provider behind auth, but in the gap between login and route navigation
  // we don't want to surface that as an error in the modal.
  const error =
    query.error instanceof ApiError && query.error.status === 401
      ? null
      : query.error
        ? query.error instanceof ApiError
          ? query.error.message
          : 'Could not load onboarding. Please refresh and try again.'
        : null;

  const value = useMemo<OnboardingContextValue>(
    () => ({
      status: query.data ?? null,
      loading: query.isPending,
      error,
      refresh,
      applyStatus,
    }),
    [query.data, query.isPending, error, refresh, applyStatus],
  );

  return (
    <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}
